import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { BookOpen, Zap, Battery, TrendingUp, AlertTriangle } from 'lucide-react';

export default function FlexEGuideModal() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <BookOpen className="h-4 w-4" />
          Handleiding
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Zap className="h-6 w-6 text-primary" />
            Flex-E Simulatie - Complete Handleiding
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] pr-4">
          <div className="space-y-6">
            
            {/* Section 1: Wat is Flex-E */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <Battery className="h-5 w-5 text-primary" />
                1. Wat is de Flex-E Simulatie?
              </h3>
              <div className="space-y-2 text-sm">
                <p>
                  De Flex-E module simuleert of een batterijsysteem voldoende is om een klant te helpen 
                  binnen een <strong>tijdelijke contractlimiet</strong> te blijven tijdens piekuren.
                </p>
                
                <div className="bg-muted p-4 rounded-lg mt-3">
                  <p className="font-semibold mb-2">Praktijkvoorbeeld:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Normaal vermogen:</strong> 235 kW (06:00-16:00 en 21:00-06:00)</li>
                    <li><strong>Beperkt vermogen:</strong> 135 kW (16:00-21:00) - piekuren</li>
                    <li><strong>Batterij:</strong> 100 kW vermogen, 400 kWh capaciteit</li>
                  </ul>
                  <p className="mt-2">
                    <strong>Doel:</strong> Controleren of de klant tussen 16:00-21:00 kan blijven draaien 
                    met 135 kW uit het net + maximaal 100 kW uit de batterij.
                  </p>
                </div>
              </div>
            </section>

            <Separator />

            {/* Section 2: Waarom is dit belangrijk */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                2. Waarom is dit kritiek?
              </h3>
              <div className="space-y-2 text-sm">
                <div className="bg-destructive/10 border-l-4 border-destructive p-4 rounded">
                  <p className="font-semibold text-destructive mb-2">Risico op stroomuitval</p>
                  <p>
                    Als het verbruik tijdens piekuren te hoog is en de batterij onvoldoende capaciteit 
                    of vermogen heeft, komt de klant <strong>letterlijk zonder stroom te zitten</strong>. 
                    Dit kan leiden tot productiestops, dataschade, of zelfs veiligheidsrisico's.
                  </p>
                </div>
                
                <p className="mt-3">
                  <strong>Twee cruciale factoren:</strong>
                </p>
                <ol className="list-decimal pl-5 space-y-2 mt-2">
                  <li>
                    <strong>Vermogen (kW):</strong> Kan de batterij snel genoeg ontladen? 
                    Als het verbruik 185 kW is en de limiet 135 kW, moet de batterij 50 kW kunnen leveren.
                  </li>
                  <li>
                    <strong>Capaciteit (kWh):</strong> Houdt de batterij het vol gedurende de hele piekperiode? 
                    Als er 5 uur lang 40 kW tekort is, moet de batterij 200 kWh kunnen leveren.
                  </li>
                </ol>
              </div>
            </section>

            <Separator />

            {/* Section 3: Berekeningen */}
            <section>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-primary" />
                3. Hoe werken de berekeningen?
              </h3>
              <div className="space-y-4 text-sm">
                
                <div>
                  <h4 className="font-semibold mb-2">3.1 Energiebehoefte (kWh)</h4>
                  <p>Per kwartier wordt berekend hoeveel energie de batterij moet leveren:</p>
                  <code className="block bg-muted p-3 rounded mt-2 font-mono text-xs">
                    Als verbruik &gt; contractlimiet:<br/>
                    tekort_kw = verbruik - contractlimiet<br/>
                    energie_kwh = tekort_kw × 0,25 uur<br/><br/>
                    Dagbehoefte = som van alle kwartieren
                  </code>
                  <p className="mt-2 italic">
                    Voorbeeld: 50 kW tekort gedurende 1 kwartier = 12,5 kWh
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3.2 Piekvermogen (kW)</h4>
                  <p>Het maximale moment waarop de batterij moet kunnen leveren:</p>
                  <code className="block bg-muted p-3 rounded mt-2 font-mono text-xs">
                    max_deficit_kw = MAX(verbruik - contractlimiet) per dag
                  </code>
                  <p className="mt-2 italic">
                    Voorbeeld: Als het hoogste verbruik 195 kW is en de limiet 135 kW, 
                    dan is het piekvermogen 60 kW.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3.3 Slaag/Faal Criteria</h4>
                  <p>Een dag is <strong className="text-green-600">geslaagd</strong> als:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Dagbehoefte (kWh) ≤ Batterijcapaciteit (kWh)</li>
                    <li>EN piekvermogen (kW) ≤ Batterijvermogen (kW)</li>
                  </ul>
                  <p className="mt-2">Een dag is <strong className="text-red-600">gefaald</strong> als één van beide grenzen wordt overschreden.</p>
                </div>

              </div>
            </section>

            <Separator />

            {/* Section 4: Grafieken uitleg */}
            <section>
              <h3 className="text-lg font-semibold mb-3">4. Grafiek-per-Grafiek Uitleg</h3>
              <div className="space-y-4 text-sm">
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">📊 Grafiek 1: Energiebehoefte vs. Batterijcapaciteit</h4>
                  <p><strong>Wat zie je:</strong> Staafdiagram per dag met totale energiebehoefte (kWh).</p>
                  <p className="mt-1"><strong>Stippellijn:</strong> Batterijcapaciteit (kWh)</p>
                  <p className="mt-2"><strong>Interpretatie:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Staaf onder lijn = batterij heeft genoeg energie</li>
                    <li>Staaf boven lijn = batterij raakt leeg, stroomuitval</li>
                  </ul>
                  <p className="mt-2 font-semibold text-primary">Naar klant: "Op dagen waar de staaf boven de lijn komt, zou uw bedrijf zonder stroom komen."</p>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">📈 Grafiek 2: Vermogensoverschrijding vs. Batterijvermogen</h4>
                  <p><strong>Wat zie je:</strong> Lijndiagram per dag met hoogste piekvermogen (kW).</p>
                  <p className="mt-1"><strong>Stippellijn:</strong> Batterijvermogen (kW)</p>
                  <p className="mt-2"><strong>Interpretatie:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Lijn onder lijn = batterij kan snel genoeg ontladen</li>
                    <li>Lijn boven lijn = batterij te traag, stroomuitval</li>
                  </ul>
                  <p className="mt-2 font-semibold text-primary">Naar klant: "Als de lijn boven de stippellijn komt, is de batterij te zwak om de piek op te vangen."</p>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">🎯 Grafiek 3: Dagresultaten - Vermogen vs. Energie</h4>
                  <p><strong>Wat zie je:</strong> Scatter plot waarbij elk punt één dag is.</p>
                  <p className="mt-1"><strong>Kleuren:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li className="text-green-600">🟢 Groen = dag geslaagd, geen uitval</li>
                    <li className="text-red-600">🔴 Rood = dag gefaald, risico op uitval</li>
                  </ul>
                  <p className="mt-2"><strong>Interpretatie:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Punten linksonder = gemakkelijke dagen</li>
                    <li>Punten rechtsboven = extreme dagen met hoge pieken</li>
                  </ul>
                  <p className="mt-2 font-semibold text-primary">Naar klant: "Rode punten zijn dagen waar het mis zou gaan. Clusters rechtsboven tonen wanneer uw bedrijf het zwaarst belast is."</p>
                </div>

              </div>
            </section>

            <Separator />

            {/* Section 5: Klantgesprek */}
            <section>
              <h3 className="text-lg font-semibold mb-3">5. Hoe Presenteer Je Dit Aan Klanten?</h3>
              <div className="space-y-3 text-sm">
                
                <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                  <p className="font-semibold mb-2">Opening:</p>
                  <p className="italic">
                    "We hebben uw verbruiksdata geanalyseerd om te bepalen of de voorgestelde batterij 
                    voldoende is om tijdens piekuren binnen de contractlimiet te blijven. Laten we kijken 
                    naar de resultaten..."
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Als Success Rate &gt; 95%:</p>
                  <p className="italic text-green-600">
                    "Uitstekend nieuws! De batterij voldoet op 95% van de dagen. De enkele rode dagen 
                    zijn extreme uitschieters die u kunt oplossen door die dagen anders te plannen, 
                    of we kunnen de batterij iets vergroten voor 100% zekerheid."
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Als Success Rate 80-95%:</p>
                  <p className="italic text-amber-600">
                    "De batterij is bijna voldoende, maar er zijn meerdere dagen waar het krap is. 
                    Ik raad aan om de capaciteit met 15% te verhogen om betrouwbaarheid te garanderen."
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Als Success Rate &lt; 80%:</p>
                  <p className="italic text-red-600">
                    "Deze batterij is helaas onvoldoende. U zou regelmatig zonder stroom komen. 
                    We moeten ofwel de batterij aanzienlijk vergroten, ofwel kijken naar het 
                    heronderhandelen van uw contractlimiet."
                  </p>
                </div>

                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="font-semibold mb-2">Belangrijk: Verschil tussen Contractlimiet en Vol Vermogen</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Vol vermogen (235 kW):</strong> Wat u normaal gesproken maximaal mag afnemen van het net</li>
                    <li><strong>Contractlimiet (135 kW):</strong> Tijdelijke beperking tijdens piekuren (16:00-21:00) om netcongestie te verminderen</li>
                    <li><strong>Batterij compenseert:</strong> Het verschil tussen wat u nodig heeft en de beperkte 135 kW</li>
                  </ul>
                </div>

              </div>
            </section>

            <Separator />

            {/* Section 6: Tips */}
            <section>
              <h3 className="text-lg font-semibold mb-3">6. Praktische Tips</h3>
              <div className="space-y-2 text-sm">
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Check de worst day:</strong> Kijk welke dag het slechtst scoort en waarom. 
                    Vaak is dit een productiedag met alles tegelijk aan.
                  </li>
                  <li>
                    <strong>Seizoenspatronen:</strong> Upload data van zowel zomer als winter om seizoensverschillen mee te nemen.
                  </li>
                  <li>
                    <strong>Marge aanhouden:</strong> Adviseer altijd 10-15% extra capaciteit voor onvoorziene omstandigheden.
                  </li>
                  <li>
                    <strong>C-waarde uitleggen:</strong> C=1 betekent dat de batterij in 1 uur volledig kan ontladen. 
                    C=0,5 betekent 2 uur. Hogere C-waarde = snellere ontlading maar duurder.
                  </li>
                  <li>
                    <strong>Actieve dagen:</strong> Test verschillende scenario's (alleen werkdagen vs. 7 dagen per week).
                  </li>
                </ul>
              </div>
            </section>

          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
